import { SortDirection } from "@redotech/redo-model/tables/table";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";

export const FraudZipCodeStatsSortKeySchema = z.enum([
  "zipCode",
  "location",
  "customers",
  "returns",
  "returnRate",
]);

export type FraudZipCodeStatsSortKey = z.infer<
  typeof FraudZipCodeStatsSortKeySchema
>;

export const FraudZipCodeStatsSortSchema = z.object({
  key: FraudZipCodeStatsSortKeySchema,
  direction: createEnum(SortDirection),
});

export type FraudZipCodeStatsSort = z.infer<typeof FraudZipCodeStatsSortSchema>;

export const FraudZipCodeStatsFilterKeySchema = z.enum([
  "date",
  "zipCode",
  "customers",
  "returns",
  "returnRate",
]);

export type FraudZipCodeStatsFilterKey = z.infer<
  typeof FraudZipCodeStatsFilterKeySchema
>;

export const FraudZipCodeStatsNumberFilterKeySchema = z.enum([
  "customers",
  "returns",
  "returnRate",
]);

export type FraudZipCodeStatsNumberFilterKey = z.infer<
  typeof FraudZipCodeStatsNumberFilterKeySchema
>;

export const FraudZipCodeStatsNumberFilterOperatorSchema = z.enum([
  "equal",
  "lessThan",
  "greaterThan",
  "between",
]);

export type FraudZipCodeStatsNumberFilterOperator = z.infer<
  typeof FraudZipCodeStatsNumberFilterOperatorSchema
>;

export const FraudZipCodeStatsStringFilterOperatorSchema = z.enum([
  "equals",
  "contains",
]);

export type FraudZipCodeStatsStringFilterOperator = z.infer<
  typeof FraudZipCodeStatsStringFilterOperatorSchema
>;

export const FraudZipCodeStatsNumberFilterSchema = z.object({
  key: FraudZipCodeStatsNumberFilterKeySchema,
  operator: FraudZipCodeStatsNumberFilterOperatorSchema,
  value: z.union([z.tuple([z.number()]), z.tuple([z.number(), z.number()])]),
});

export type FraudZipCodeStatsNumberFilter = z.infer<
  typeof FraudZipCodeStatsNumberFilterSchema
>;

export const FraudZipCodeStatsZipCodeFilterSchema = z.object({
  key: z.literal("zipCode"),
  operator: FraudZipCodeStatsStringFilterOperatorSchema,
  value: z.string(),
});

export type FraudZipCodeStatsZipCodeFilter = z.infer<
  typeof FraudZipCodeStatsZipCodeFilterSchema
>;

export const FraudZipCodeStatsDateFilterSchema = z.object({
  key: z.literal("date"),
  startDate: z.string(),
  endDate: z.string(),
});

export type FraudZipCodeStatsDateFilter = z.infer<
  typeof FraudZipCodeStatsDateFilterSchema
>;

export const FraudZipCodeStatsFilterSchema = z.union([
  FraudZipCodeStatsDateFilterSchema,
  FraudZipCodeStatsNumberFilterSchema,
  FraudZipCodeStatsZipCodeFilterSchema,
]);

export type FraudZipCodeStatsFilter = z.infer<
  typeof FraudZipCodeStatsFilterSchema
>;

export const FraudZipCodeStatsSchema = z.object({
  zipCode: z.string(),
  location: z.string().nullable(),
  customers: z.number(),
  returns: z.number(),
  returnRate: z.number(),
});

export type FraudZipCodeStats = z.infer<typeof FraudZipCodeStatsSchema>;

export const getFraudZipCodeStats = {
  name: "getFraudZipCodeStats" as const,
  input: z.object({
    page: z.number().int().min(1).default(1),
    pageSize: z.number().int().min(1).max(500).default(50),
    search: z.string().optional(),
    sort: FraudZipCodeStatsSortSchema.default({
      key: "returnRate",
      direction: SortDirection.DESC,
    }),
    filters: z.array(FraudZipCodeStatsFilterSchema).default([]),
  }),
  output: z.object({
    zipCodeStats: z.array(FraudZipCodeStatsSchema),
    totalCount: z.number(),
  }),
};
